import React, { useEffect, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Config from "../../Config";
import { setAllClear } from "../../../store/Actions/authActions";
import { clearCart, setClearCartState } from "../../../store/Actions/cartActions";
import { serverError } from "../../../utils/password";
import { AccountLayout, BackToSignIn, PasswordForm, StatusBadge } from "../ResetPassword/shared";

/**
 * /new-user-password?token=… — compte créé pour le client (par un manager,
 * au comptoir…) : il choisit son premier mot de passe.
 *
 * Comme avant : la session éventuellement ouverte dans ce navigateur est
 * vidée (c'est peut-être celle de quelqu'un d'autre), le jeton est vérifié
 * (GET /customer/valid_token), puis PUT /customer/update_password.
 */
const NewUserPassword = ({ location, dispatch }) => {
  const { t } = useTranslation();
  const [token] = useState(() => new URLSearchParams(location?.search).get("token"));
  const [status, setStatus] = useState(token ? "checking" : "invalid");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    localStorage.clear();
    dispatch(setAllClear());
    dispatch(setClearCartState());
    dispatch(clearCart());
    if (!token) return;
    Axios.get(`${Config.apiURL}/customer/valid_token`, { ...Config.headers, params: { token } })
      .then(() => setStatus("form"))
      .catch(() => setStatus("invalid"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (password, confirmation) => {
    setSubmitting(true);
    setError(null);
    try {
      await Axios.put(
        `${Config.apiURL}/customer/update_password`,
        { password, password_confirmation: confirmation, token },
        Config.headers
      );
      setStatus("done");
    } catch (err) {
      setError(serverError(err) || t("Network error, please try again later."));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "checking") {
    return (
      <AccountLayout t={t}>
        <span className="wosh-auth__spinner" aria-busy="true" />
      </AccountLayout>
    );
  }

  if (status === "invalid") {
    return (
      <AccountLayout t={t}>
        <StatusBadge icon="i-lock" tone="warning" />
        <h1 className="wosh-auth__title">{t("This link doesn't work")}</h1>
        <p className="wosh-auth__lede">
          {t("It may have expired or already been used. Reset your password to get a new one.")}
        </p>
        <Link to="/forgot-password" className="wosh-auth__cta wosh-acc__cta">
          {t("Reset my password")}
        </Link>
        <BackToSignIn t={t} />
      </AccountLayout>
    );
  }

  if (status === "done") {
    return (
      <AccountLayout t={t}>
        <StatusBadge icon="i-check" tone="success" />
        <h1 className="wosh-auth__title">{t("Your password is ready")}</h1>
        <p className="wosh-auth__lede">{t("Sign in to see your orders and book your next pickup.")}</p>
        <Link to="/sign-in" className="wosh-auth__cta wosh-acc__cta">
          {t("Sign in")}
        </Link>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout t={t}>
      <h1 className="wosh-auth__title">{t("Create your password")}</h1>
      <p className="wosh-auth__lede">{t("Your WOSH account is ready. Choose a password to sign in.")}</p>
      {error && <div className="wosh-auth__alert">{error}</div>}
      <PasswordForm t={t} cta={t("Create my password")} submitting={submitting} onSubmit={submit} />
    </AccountLayout>
  );
};

export { NewUserPassword };
export default connect()(NewUserPassword);
